import { useEffect, useState } from 'react';
import { AuthPage } from './features/auth/components/AuthPage';
import { LandingPage } from './features/landing/LandingPage';
import { TaskBoard } from './features/tasks/components/TaskBoard/TaskBoard';
import { TeamDropdown } from './features/teams/components/TeamDropdown/TeamDropdown';
import { TeamManager } from './features/teams/components/TeamManager/TeamManager';
import { AdminPanel } from './features/admin/components';
import { DialogProvider } from './components/ui/Dialog';
import { ToastContainer } from './components/ui/Toast';
import { useAuthStore } from './stores/authStore';
import { useToastStore } from './stores/toastStore';
import { useWorkspaceStore } from './stores/workspaceStore';
import { websocketService } from './services/websocket';
import { useTaskSync } from './hooks/useTaskSync';
import type { TaskStatus, Team, TeamRole } from './types';
import {
  WaveIcon,
  CheckCircleIcon,
  ConstructionIcon,
  InfoIcon,
  CalendarIcon,
  TeamIcon,
  UserIcon,
  TaskBoardIcon
} from './components/ui/Icons';

function App() {
  const { isAuthenticated, initialize, user, team, logout, isAdmin } = useAuthStore();
  const { messages, removeToast } = useToastStore();
  const { currentWorkspace } = useWorkspaceStore();
  const [showTaskTypeManager, setShowTaskTypeManager] = useState(false);
  const [showTeamManager, setShowTeamManager] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showGoalSidebar, setShowGoalSidebar] = useState(false);
  const [showPausedGoalTasks, setShowPausedGoalTasks] = useState(false);
  const [selectedPausedGoalId, setSelectedPausedGoalId] = useState<string | null>(null);
  const [pausedGoalTargetElement, setPausedGoalTargetElement] = useState<HTMLElement | null>(null);
  const [currentTeam, setCurrentTeam] = useState<Team | null>(team);
  // 未登录时：null = 看落地页；'login' / 'register' = 进入对应表单（地址带 #login / #register 也直接进）
  const [authView, setAuthView] = useState<null | 'login' | 'register'>(() => {
    const h = typeof window !== 'undefined' ? window.location.hash.replace('#', '') : '';
    return h === 'login' || h === 'register' ? h : null;
  });
  const [currentUserRole, setCurrentUserRole] = useState<TeamRole>('MEMBER'); // TODO: 从API获取真实角色

  // 启用任务实时同步
  useTaskSync();

  // 处理添加任务
  const handleAddTask = (status: TaskStatus) => {
    // TODO: 实现添加任务功能
  };

  // 初始化认证状态
  useEffect(() => {
    initialize();
  }, [initialize]);

  // 初始化WebSocket连接
  useEffect(() => {
    if (!user) return;

    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('未找到token，无法建立WebSocket连接');
      return;
    }

    const socket = websocketService.connect(token);

    // 监听WebSocket连接成功事件，连接成功后立即加入工作空间
    const handleConnect = () => {
      // 延迟一小段时间确保workspaceStore已初始化
      setTimeout(() => {
        const workspace = currentWorkspace.type;
        const teamId = currentWorkspace.type === 'team' ? currentWorkspace.teamId : undefined;
        const workspaceName = workspace === 'team' ? (currentWorkspace.teamName || '团队') : '个人';

        websocketService.joinWorkspace(workspace, teamId)
          .catch((error) => {
            console.error('加入工作空间失败:', error);
          });
      }, 100);
    };

    // 如果已经连接，立即加入房间
    if (socket.connected) {
      handleConnect();
    } else {
      // 否则等待连接成功
      socket.on('connect', handleConnect);
    }

    // 清理函数：退出登录时断开WebSocket
    return () => {
      socket.off('connect', handleConnect);
      websocketService.disconnect();
    };
  }, [user]);

  // 监听工作空间切换，自动切换WebSocket房间
  useEffect(() => {
    if (!user || !websocketService.isConnected()) return;

    const workspace = currentWorkspace.type;
    const teamId = currentWorkspace.type === 'team' ? currentWorkspace.teamId : undefined;
    const workspaceName = workspace === 'team' ? (currentWorkspace.teamName || '团队') : '个人';

    // 加入新的工作空间房间
    websocketService.joinWorkspace(workspace, teamId)
      .then(() => {
        console.log(`工作空间已切换: ${workspaceName}`);
      })
      .catch((error) => {
        console.error('切换工作空间失败:', error);
      });
  }, [currentWorkspace, user]);

  // 同步团队状态
  useEffect(() => {
    setCurrentTeam(team);
  }, [team]);

  // 快捷键监听 - Ctrl+B 切换目标侧边栏
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 'b') {
        event.preventDefault();
        setShowGoalSidebar(prev => !prev);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // 监听目标侧栏状态，关闭时自动关闭暂停目标任务面板
  useEffect(() => {
    if (!showGoalSidebar && showPausedGoalTasks) {
      setShowPausedGoalTasks(false);
      setSelectedPausedGoalId(null);
      setPausedGoalTargetElement(null);
    }
  }, [showGoalSidebar, showPausedGoalTasks]);

  // 处理团队更新
  const handleTeamUpdate = (updatedTeam: Team) => {
    setCurrentTeam(updatedTeam);
    // TODO: 更新authStore中的团队信息
  };

  // 处理暂停目标点击
  const handlePausedGoalClick = (goalId: string, targetElement?: HTMLElement) => {
    setSelectedPausedGoalId(goalId);
    setPausedGoalTargetElement(targetElement || null);
    setShowPausedGoalTasks(true);
  };

  // 处理普通目标选择（会关闭暂停目标面板）
  const handleGoalSelect = (goalId: string | null) => {
    // 当选择普通目标时，关闭暂停目标面板
    if (showPausedGoalTasks) {
      setShowPausedGoalTasks(false);
      setSelectedPausedGoalId(null);
    }
  };

  // 处理关闭暂停目标任务面板
  const handleClosePausedPanel = () => {
    setShowPausedGoalTasks(false);
    setSelectedPausedGoalId(null);
    setPausedGoalTargetElement(null);
  };

  const authenticated = isAuthenticated();


  return (
    <div className="min-h-screen bg-neutral-75">
      {!authenticated ? (
        authView ? (
          <AuthPage initialMode={authView} onBack={() => { setAuthView(null); window.history.replaceState(null, '', window.location.pathname); }} />
        ) : (
          <LandingPage onLogin={() => setAuthView('login')} onRegister={() => setAuthView('register')} />
        )
      ) : (
        // 主应用界面
        <div>
          {/* Header */}
          <header className="bg-slate-35 shadow-sm border-b border-neutral-100">
            <div className="max-w-[1600px] mx-auto px-3 sm:px-6 lg:px-10">
              <div className="flex justify-between items-center h-16">
                <div className="flex items-center flex-shrink-0">
                  <img src="/mengdong-mark.webp" alt="蒙懂" className="w-8 h-auto sm:h-7 sm:w-auto mr-2 sm:mr-3 object-contain" />
                  <h1 className="text-lg sm:text-xl font-semibold text-gray-700 whitespace-nowrap">蒙懂看板</h1>
                  <span className="hidden sm:inline ml-4 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full">
                    {new Date().toLocaleDateString('zh-CN', {
                      month: 'long',
                      day: 'numeric',
                      weekday: 'long'
                    })}
                  </span>
                </div>
                
                <div className="flex items-center min-w-0 space-x-1 sm:space-x-4">
                  {/* 工作空间下拉菜单 */}
                  <TeamDropdown
                    currentTeam={currentTeam}
                    onManageClick={() => setShowTeamManager(true)}
                  />

                  {/* 目标管理按钮 */}
                  <button
                    onClick={() => setShowGoalSidebar(prev => !prev)}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                    title="目标管理"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                    </svg>
                  </button>

                  {/* 系统管理按钮 - 仅管理员可见 */}
                  {isAdmin() && (
                    <button
                      onClick={() => setShowAdminPanel(true)}
                      className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                      title="系统管理"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    </button>
                  )}

                  {/* 设置按钮 */}
                  <button
                    onClick={() => setShowTaskTypeManager(true)}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
                    title="管理任务类型"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </button>

                  <div className="flex items-center flex-shrink-0 space-x-2">
                    <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white text-sm font-medium">
                      {user?.username.charAt(0).toUpperCase()}
                    </div>
                    <span className="hidden sm:inline text-gray-700 font-medium">{user?.username}</span>
                    <button
                      onClick={logout}
                      className="text-gray-500 hover:text-gray-700 sm:ml-2 whitespace-nowrap"
                    >
                      退出
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </header>

          {/* 主内容区域 */}
          <TaskBoard
            onAddTask={handleAddTask}
            showTaskTypeManager={showTaskTypeManager}
            setShowTaskTypeManager={setShowTaskTypeManager}
            showGoalSidebar={showGoalSidebar}
            setShowGoalSidebar={setShowGoalSidebar}
            showPausedGoalTasks={showPausedGoalTasks}
            setShowPausedGoalTasks={setShowPausedGoalTasks}
            selectedPausedGoalId={selectedPausedGoalId}
            pausedGoalTargetElement={pausedGoalTargetElement}
            onPausedGoalClick={handlePausedGoalClick}
            onGoalSelect={handleGoalSelect}
            onClosePausedPanel={handleClosePausedPanel}
          />
          
          {/* 团队管理模态框 */}
          {showTeamManager && user && (
            <TeamManager
              isOpen={showTeamManager}
              onClose={() => setShowTeamManager(false)}
              currentTeam={currentTeam} // 可以是 null
              currentUser={user}
              currentUserRole={currentUserRole}
              onTeamUpdate={handleTeamUpdate}
            />
          )}

          {/* 系统管理面板 - 仅管理员可见 */}
          {showAdminPanel && isAdmin() && user && (
            <AdminPanel
              isOpen={showAdminPanel}
              onClose={() => setShowAdminPanel(false)}
              currentUser={user}
            />
          )}


          {/* 版权声明 - 固定在页面底部 */}
          <footer className="fixed bottom-0 left-0 right-0 bg-neutral-25 bg-opacity-95 backdrop-blur-sm border-t border-neutral-100 z-50">
            <div className="text-center py-3">
              <p className="text-xs text-gray-400">
                © 2026{' '}
                <a
                  href="https://mengdong.video"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-500 transition-colors"
                >
                  蒙懂AI
                </a>
              </p>
            </div>
          </footer>
        </div>
      )}

      {/* 全局对话框 */}
      <DialogProvider />

      {/* Toast通知 */}
      <ToastContainer messages={messages} onRemove={removeToast} />
    </div>
  );
}

export default App;
